// Simplified Chinese copy for the pricing pages ("您", like the site's zh locale). Same shape as copy.en.ts (the
// source of truth); keep the two in step. Plan names (Lite, Pro, Max, Pay as you go, Enterprise) are product names
// and stay in English, as they do in the app. "按需 FTP", "网页上传工具", "预览图优先", "画廊", "工作区" and "计划"
// as on /on-demand-ftp and /preview-first-uploads.

import type { PricingCopy } from "./copy.en";

const zh: PricingCopy = {
  common: {
    perMonth: "/月",
    onDemandFtpLink: "按需 FTP 是如何运作的",
    aMonth: "每月",
    today: "现在",
    fromChangeDate: "11月1日起",
    changeDate: "2026年11月1日",
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "最受欢迎",
    renamedPlan: "{from}（现更名为 {to}）",
    planFeatures: {
      free: [
        {
          text: "您自己的品牌，带“Powered by Photohawk”标识",
          note: "每个画廊都显示您的标志和品牌色。画廊和工作区登录页底部会显示一个小小的“Powered by Photohawk”徽章。Pro 和 Max 没有徽章。",
        },
        "团队最多 3 人",
        "帮助中心支持",
        {
          text: "按需 FTP 服务器",
          note: "如果一定要用 FTP，可以在网页上传工具中启动您自己的服务器，使用永不改变的专属 Photohawk 地址。无活动 1 小时后自动关闭。",
        },
        {
          text: "预览图优先上传",
          note: "画廊几分钟内就能凭轻量预览图上线，原图随后跟上，销售和免费下载都适用。",
        },
      ],
      pro: [
        {
          text: "您自己的品牌",
          note: "显示您的标志和品牌色，没有 Photohawk 徽章。",
        },
        "自定义域名",
        "团队最多 10 人",
        "访客分析",
        {
          text: "按需 FTP 服务器",
          note: "如果一定要用 FTP，可以在网页上传工具中启动您自己的服务器，使用永不改变的专属 Photohawk 地址。无活动 1 小时后自动关闭。",
        },
        {
          text: "预览图优先上传",
          note: "画廊几分钟内就能凭轻量预览图上线，原图随后跟上，销售和免费下载都适用。",
        },
        "邮件支持",
      ],
      max: [
        "包含 Pro 的全部功能",
        "视频上传",
        "收入分成",
        "多个品牌",
        "不限团队成员和角色",
        "访客分析",
        {
          text: "按需 FTP 服务器",
          note: "如果一定要用 FTP，可以在网页上传工具中启动您自己的服务器，使用永不改变的专属 Photohawk 地址。无活动 1 小时后自动关闭。",
        },
        {
          text: "预览图优先上传",
          note: "画廊几分钟内就能凭轻量预览图上线，原图随后跟上，销售和免费下载都适用。",
        },
        "优先支持",
      ],
    },
    planTaglines: {
      free: "适合刚起步和偶尔拍摄活动。",
      pro: "适合以自有品牌定期销售的工作区。",
      max: "适合工作室、团队和大批量业务：最低佣金、最多存储空间、视频和优先支持。",
    },
    countries: {
      uk: "英国",
      my: "马来西亚",
      ph: "菲律宾",
      mx: "墨西哥",
      co: "哥伦比亚",
      eur: "葡萄牙和欧元区",
      us: "美国",
      ca: "加拿大",
    },
    inCountry: {
      uk: "在英国",
      my: "在马来西亚",
      ph: "在菲律宾",
      mx: "在墨西哥",
      co: "在哥伦比亚",
      eur: "在葡萄牙和欧元区",
      us: "在美国",
      ca: "在加拿大",
    },
  },

  pricing: {
    seo: {
      title: "价格 | Photohawk",
      description:
        "活动摄影的每个计划佣金都更低：Lite 16%、Pro 14%、Max 12%。包含存储空间和免费下载。",
    },
    notice: "新价格自 2026年11月1日起生效。",
    noticeLink: "查看变化",
    hero: {
      badge: "价格",
      title: "每个计划的佣金都更低。",
      titleHighlight: "最低仅 {rate}。",
      subtitle:
        "Lite 按 16% 销售，Pro 按 14%，Max 按 12%。交易手续费与现在完全相同。",
      events: "各类活动都适用同样的计划：跑步和骑行、比赛、演出、音乐节、毕业典礼和会议。",
    },
    rate: {
      heading: "各计划的佣金",
      planLabel: "{plan}",
      points: [
        "银行卡手续费另计，与现在完全相同。",
        "我们的佣金是每笔销售中 Photohawk 收取的唯一费用。",
        "现有的定制协议在到期前继续有效。",
      ],
    },
    plans: {
      heading: "选择您的计划",
      subtitle:
        "以美元计价的月付计划，无合约约束。每升一级，佣金更低，存储空间和免费下载更多。",
      sellingLabel: "销售",
      sellingLine: "销售佣金 {rate}",
      sellingNote: "另加交易手续费，与现在相同",
      freeDownloadsLabel: "免费下载",
      freeDownloadsLine: "每月含 {count} 张照片",
      overageLine: "超出后每 1,000 张 $8",
      storageLabel: "存储空间",
      storageLine: "含 {storage}",
      storageNote: "超出后存储包每月 $5 起",
      includedLabel: "包含",
      cta: "开始使用",
      ctaFor: "{plan}",
      planChange:
        "更换计划：计划的较低佣金从您使用该计划的第一个完整月开始生效。降级在月底生效。",
    },
    standard: {
      heading: "每个计划都标配",
      subtitle: "每个计划都包含这些功能，包括每月 $0 的 Lite。",
      items: [
        { id: "selfie", title: "自拍搜索", body: "买家自拍一张，就能看到自己出现的每张照片。" },
        { id: "bib", title: "号码布和时间搜索", body: "按比赛号码或拍摄时间查找照片。" },
        { id: "preview", title: "预览保护", body: "防截图保护，轻点才显示，让预览图带来销售而不是被复制。" },
        {
          id: "uploader",
          title: "网页上传工具和 Photohawk Studio",
          body: "标准的上传方式：同时发送大量照片，自动适应您的网络，中断后自动续传。所有计划都可以先上传轻量预览图，让您的画廊几分钟内上线。Studio 现在可在网页浏览器中使用；适用于 Mac、iPhone 和 iPad 的 Photohawk Studio 应用将于 2026年11月推出。",
        },
        { id: "whatsapp", title: "WhatsApp 交付，Photohawk 为买家提供的服务", body: "买家可以在结账时多付一点费用，通过 WhatsApp 接收照片，方便分享。Photohawk 在每个画廊中提供此服务：您无需设置、定价或关闭。" },
      ],
    },
    storage: {
      heading: "存储空间",
      subtitle:
        "每个计划都包含存储空间。存储更多内容时，如果已绑定银行卡，系统会自动添加存储包：按覆盖您存储量的最便宜组合计费。没有绑定银行卡？新上传会暂停，直到您添加一张；不会删除任何内容，您的画廊也会继续销售。",
      includedHeading: "包含",
      packsHeading: "额外存储包",
      packPrice: "每月 ${price}",
      examplesHeading: "存储包如何叠加",
      example: "{plan} 存储 {stored}：超出 {over}，按 {mix} 计费 = 每月 ${price}",
      note: "存储空间用到 80% 时，我们会发邮件通知您。释放空间后，存储包会自动取消。",
    },
    freeDownloads: {
      heading: "免费下载画廊",
      subtitle:
        "分享可供买家免费下载的画廊。每个计划每月都包含一定数量的照片；超出后，所有计划均为每 1,000 张 $8（每张 $0.008）。",
      line: "每月 {count} 张照片",
      note: "额度每月重置，超出后画廊也会继续正常运行。",
    },
    example: {
      heading: "您能拿到多少",
      subtitle: "一笔在英国用银行卡支付、通过您自己的 Stripe 账户收款的订单，在各计划下的情况。试试其他金额。",
      orderLabel: "订单金额",
      order: "订单",
      cardFee: "Stripe 银行卡手续费",
      cardFeeNote: "由 Stripe 向您的账户收取，例如英国银行卡 1.5% + 20p",
      colPlan: "计划",
      colCommission: "Photohawk 佣金",
      colKeep: "您约可拿到",
      planCell: "{plan}（{rate}）",
      note: "Stripe 的手续费取决于银行卡：国际卡和货币转换通常费用更高。我们的佣金是 Photohawk 收取的唯一费用。",
    },
    cardFees: {
      heading: "银行卡手续费如何计算",
      intro: "银行卡和支付处理手续费与现在完全相同，与我们的佣金分开计算。",
      items: [
        {
          id: "stripe",
          title: "使用您自己的 Stripe 账户",
          body: "在英国、欧盟、美国和其他支持 Stripe 的国家，Stripe 会直接向您的 Stripe 账户收取其标准支付处理费。",
        },
        {
          id: "managed",
          title: "由 Photohawk 代您处理付款",
          body: "在马来西亚、菲律宾、墨西哥、哥伦比亚、厄瓜多尔以及其他买家使用本地钱包付款的国家，支付处理费会按基于成本的本地费率扣除，与现在相同。",
        },
      ],
      linkLabel: "查看各国的银行卡手续费",
    },
    compare: {
      heading: "比较全部功能",
      subtitle: "Photohawk Studio 的全部功能，逐个计划比较。现在所有计划都可以在网页浏览器中使用 Studio。",
      featureColumn: "功能",
      included: "包含",
      notIncluded: "不包含",
      linkLabel: "比较全部功能",
      sections: {
        selling: "销售",
        finding: "查找照片",
        galleries: "画廊",
        brand: "您的品牌",
        insights: "数据洞察",
        team: "团队",
        uploading: "上传",
        storage: "存储空间",
        support: "支持",
      },
      rows: {
        commission: { label: "销售佣金", note: "我们在每笔销售中收取的唯一费用。银行卡手续费与现在完全相同。" },
        products: { label: "数字下载、冲印和产品", note: "您自己的产品和价格。" },
        packages: { label: "套餐和组合", note: "" },
        coupons: { label: "优惠券", note: "" },
        preorder: { label: "预订价格", note: "" },
        whatsapp: { label: "WhatsApp 交付（Photohawk 为买家提供的服务）", note: "买家在结账时付费的可选附加服务，照片会发送到 WhatsApp，方便分享。由 Photohawk 在每个画廊中提供，不是由您控制的设置。" },
        payouts: { label: "提现", note: "付款到您自己的 Stripe 账户，或在我们代您处理付款的地区由 Photohawk 支付给您。" },
        revenueSplit: { label: "收入分成", note: "与摄影师、团队、俱乐部或赞助商分享图库的销售额。Photohawk 计算每一份分成并代您支付。" },
        selfie: { label: "自拍搜索", note: "" },
        bib: { label: "号码布和文字搜索", note: "按比赛号码或照片中的文字查找照片。" },
        time: { label: "时间搜索", note: "" },
        find: { label: "在“查找照片”中展示", note: "买家可以在 photohawk.com 上找到您的公开画廊。" },
        profiles: { label: "已保存的搜索", note: "保存自拍、号码或文字，查看新的匹配结果。" },
        faceData: { label: "自拍搜索的人脸数据", note: "自 2026年11月1日起，默认永不过期，或由您选择保留期限。" },
        galleries: { label: "画廊和合集", note: "数量不限，按需创建。" },
        private: { label: "私密画廊和密码", note: "" },
        protection: { label: "预览保护", note: "防截图保护，轻点才显示。" },
        watermarks: { label: "水印", note: "" },
        freeDownloads: { label: "包含的免费下载照片", note: "额度每月重置。" },
        freeOverage: { label: "超出额度的免费下载照片", note: "画廊继续正常运行。" },
        video: { label: "视频上传", note: "" },
        address: { label: "您的 Photohawk 地址", note: "yourname.photohawk.com" },
        customDomain: { label: "自定义域名", note: "使用您自己的网址展示画廊。" },
        pages: { label: "页面", note: "在画廊旁边创建您自己的页面。" },
        emails: { label: "自定义邮件模板", note: "编辑买家从每个画廊收到的邮件。" },
        logo: { label: "您的标志和品牌色", note: "显示在您的画廊、页面和邮件中。" },
        noBadge: { label: "无“Powered by Photohawk”徽章", note: "Lite 会在画廊和登录页底部显示一个小徽章。" },
        live: { label: "实时访客", note: "当前有多少人正在浏览某个画廊。" },
        analytics: { label: "访客分析", note: "按画廊查看用户数和访问量的变化。" },
        sales: { label: "订单和销售趋势", note: "" },
        collectionDownloads: { label: "按合集统计下载量", note: "" },
        people: { label: "工作区成员", note: "画廊协作者也计入在内。已在工作区中的成员保留访问权限。" },
        roles: { label: "角色", note: "所有者、管理员、仅上传和仅销售权限。" },
        collaborators: { label: "画廊协作者", note: "" },
        webUploader: { label: "网页上传工具", note: "同时发送大量照片，中断后自动续传。" },
        previewsFirst: { label: "预览图优先", note: "画廊几分钟内就能凭轻量预览图上线，原图随后跟上。" },
        soldFirst: { label: "已售照片优先", note: "原图上传前就被购买或下载的照片会优先上传。" },
        originalsBy: { label: "面向买家的“原图交付日期”", note: "告诉买家原图何时就绪。" },
        ftp: { label: "按需 FTP 服务器", note: "如果一定要用 FTP：在网页上传工具中启动您自己的服务器。无活动 1 小时后自动关闭。" },
        cloudImport: { label: "从 Dropbox 和 Google 云端硬盘导入", note: "" },
        studioApp: { label: "适用于 Mac、iPhone 和 iPad 的 Photohawk Studio 应用", note: "现在可在网页浏览器中使用 Studio。" },
        included: { label: "包含的存储空间", note: "" },
        packs: { label: "额外存储空间", note: "绑定银行卡后自动添加，按最便宜的组合计费。" },
        you: { label: "为您的工作区提供支持", note: "" },
        buyers: { label: "由 Photohawk 回复您的买家", note: "买家的问题会发送到 Photohawk 支持团队，除非您设置了自己的地址。" },
      },
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "每月 {n}",
        upToPeople: "最多 {n} 人",
        unlimited: "不限",
        upToDomains: "最多 {n} 个",
        months: "{n} 个月",
        fromNovember: "2026年11月起",
        packs: "每 {gb} GB 每月 ${usd} 起",
        overage: "每 1,000 张 ${usd}",
        supportHelp: "帮助中心",
        supportEmail: "邮件",
        supportPriority: "优先",
      },
    },
    faq: {
      heading: "常见问题",
      items: [
        {
          q: "价格包含银行卡手续费吗？",
          a: "不包含。银行卡手续费与现在一样由支付服务商收取：由 Stripe 向您自己的账户收取，或在 Photohawk 代您处理付款的地区按基于成本的本地费率扣除。我们的佣金（Lite 16%、Pro 14%、Max 12%）是 Photohawk 收取的唯一费用。",
        },
        {
          q: "包含自拍搜索吗？",
          a: "包含，所有计划都有，包括 Lite。号码布和时间搜索、预览保护以及网页上传工具也都包含。适用于 Mac、iPhone 和 iPad 的 Photohawk Studio 应用于 2026年11月推出后，所有计划均可使用。",
        },
        {
          q: "可以随时更换计划吗？",
          a: "可以。计划按月付费，没有年度合约。换到佣金更低的计划后，该佣金从您使用该计划的第一个完整月开始生效。降级在月底生效。",
        },
        {
          q: "我有定制协议，会有变化吗？",
          a: "不会。现有的定制协议在到期前继续有效。",
        },
        {
          q: "免费下载超出额度后会怎样？",
          a: "您的免费下载画廊会继续正常运行。您的计划每月包含的照片数为：Lite 1,000 张，Pro 5,000 张，Max 10,000 张。超出后，所有计划均为每 1,000 张 $8（每张 $0.008）。",
        },
        {
          q: "如果需要更多存储空间怎么办？",
          a: "Lite 包含 150 GB，Pro 包含 1 TB，Max 包含 3 TB。超出后，如果已绑定银行卡，系统会自动添加存储包：+100 GB 每月 $5，+1 TB 每月 $25，+5 TB 每月 $100，按覆盖您存储量的最便宜组合计费。没有绑定银行卡时，新上传会暂停，直到您添加一张：不会删除任何内容，您的画廊也会继续销售。",
        },
        {
          q: "Max 包含什么？",
          a: "最低的 12% 佣金，另有 3 TB 存储空间、每月 10,000 张免费下载照片、视频上传、不限团队成员、访客分析和优先支持。",
        },
        {
          q: "我的工作区可以有多少人？",
          a: "Lite 最多 3 人，Pro 最多 10 人，Max 不限人数。工作区里已经超过这个人数？他们会保留访问权限：人数限制只在您邀请新成员时适用。",
        },
        {
          id: "ftp",
          q: "还能通过 FTP 上传吗？",
          a: "可以，如果一定要用 FTP。网页上传工具现在是所有计划的标准上传方式：它可以同时发送大量照片，远多于 FileZilla（默认同时 2 张，最多不超过 10 张）这样的 FTP 软件，连接中断后还会自动续传。如果您仍然需要 FTP，所有计划的工作区成员都可以在网页上传工具中启动自己的按需 FTP 服务器，使用永不改变的个人 Photohawk 地址；无活动 1 小时后自动关闭。共享 FTP 服务器于 2026年11月1日关闭。",
        },
        {
          q: "Photohawk Friends & Family 属于某个计划吗？",
          a: "不属于。Friends & Family 是我们为您家人的照片打造的应用，是一款即将推出的独立应用，不属于任何 Studio 计划。要管理画廊和上传？请使用 Photohawk Studio：现在所有计划都可以在网页浏览器中使用，适用于 Mac、iPhone 和 iPad 的 Studio 应用将于 2026年11月推出。",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "价格将于 2026年11月1日调整 | Photohawk",
      description:
        "自 2026年11月1日起，Photohawk 每个计划的佣金都将下调，Pro 和 Max 获得更多存储空间，每月还包含免费下载照片。",
    },
    hero: {
      badge: "价格调整",
      title: "价格将于 2026年11月1日调整。",
      titleHighlight: "每个计划的佣金都更低。",
      subtitle:
        "每个计划的佣金都下调，Pro 和 Max 获得更多存储空间，每月还包含免费下载照片。您无需任何操作。",
      ctaPrimary: "查看新价格",
      ctaSecondary: "比较您的计划",
    },
    why: {
      heading: "有哪些改进",
      items: [
        {
          id: "rates",
          title: "每个计划的佣金都更低",
          body: "每个计划的佣金下调 1.5 到 9 个百分点。Pay as you go 和 Lite 合并为新的 Lite，无月费，佣金 16%；Pro 降至 14%；Enterprise（现更名为 Max）降至 12%。",
        },
        {
          id: "storage",
          title: "每个计划的存储空间都更多",
          body: "Lite 从 100 GB 增至 150 GB，Pro 从 500 GB 增至 1 TB，Max 从 1 TB 增至 3 TB。Pay as you go 的 25 GB 变为 Lite 的 150 GB。",
        },
        {
          id: "downloads",
          title: "包含免费下载照片",
          body: "目前，免费下载画廊中的每张照片都要收费。自 11月1日起，每月包含 1,000 到 10,000 张，超出后每 1,000 张 $8：在每个计划下都与现在持平或更低。",
        },
        {
          id: "simple",
          title: "更简单的价格",
          body: "计划从四个减为三个，每个计划一个佣金，额外存储空间采用简单的存储包。",
        },
        {
          id: "order",
          title: "交易手续费不变",
          body: "交易手续费与现在完全相同，与我们的佣金分开计算。",
        },
        {
          id: "deals",
          title: "您的协议继续有效",
          body: "如果您有定制协议，它将在到期前继续有效。",
        },
      ],
      honestHeading: "唯一上涨的价格",
      honestBody:
        "Enterprise（现更名为 Max）从 11月1日当天或之后的第一次续订起为每月 $49（原为 $44.99）。相应地，它包含 3 TB 而不是 1 TB 存储空间、每月 10,000 张免费下载照片，以及最低的佣金：12%，而不是 13.5%。只要您每月销售额约达 $270，更低的佣金就足以抵消差价。Pro 从 $29.99 降至 $29。",
    },
    when: {
      heading: "11月1日会发生什么",
      subtitle: "所有人同一天生效。您的工作区会自动转到新的计划。",
      moves: {
        payg: "仍为每月 $0。佣金从 25% 降至 16%，存储空间从 25 GB 增至 150 GB。",
        lite: "Lite 保留，并且现在免费：不再收取每月 $16.99，佣金从 18.5% 降至 16%，存储空间也更多：从 100 GB 增至 150 GB。如需更多空间，额外存储每 100 GB 每月 $5 起。您的画廊会保留您的标志和品牌色，并显示一个小小的“Powered by Photohawk”徽章。",
        pro: "每月 $29，而不是 $29.99；佣金从 16% 降至 14%；存储空间从 500 GB 增至 1 TB。",
        enterprise: "Enterprise 更名为 Max — 同一个计划，新名字，现在包含 3 TB 存储空间和 12% 的佣金。从 11月1日当天或之后的第一次续订起，价格为每月 $49，而不是 $44.99。",
      },
      points: [
        {
          id: "action",
          title: "无需任何操作",
          body: "您的工作区将于 2026年11月1日转换。画廊、价格和提现照常进行。",
        },
        {
          id: "month",
          title: "完整月规则",
          body: "换到佣金更低的计划后，该佣金从您使用该计划的第一个完整月开始生效。降级在月底生效。",
        },
        {
          id: "change",
          title: "更换计划",
          body: "您可以随时在工作区的“账单”中更换计划。",
        },
        {
          id: "deals",
          title: "定制协议",
          body: "现有的定制协议在到期前继续有效。",
        },
        {
          id: "privacy",
          title: "自拍搜索与隐私",
          body: "自 11月1日起，由您决定自拍搜索所用的人脸数据保留多久：默认永不过期，或在每张照片上传后保留一段时间。您的照片、销售和号码布搜索不受影响。",
        },
        {
          id: "team",
          title: "您的团队",
          body: "Lite 最多 3 人，Pro 最多 10 人，Max 不限人数。工作区里已经超过这个人数？他们会保留访问权限 — 人数限制只在添加新成员时适用。",
        },
      ],
    },
    ftp: {
      date: "2026年11月1日",
      heading: "网页上传工具现在是标准的上传方式",
      body: "一直在线的共享 FTP 服务器将于 2026年11月1日关闭。网页上传工具比 FTP 更快，能充分利用您的网络，所有计划均可使用。如果一定要用 FTP，您可以按需启动自己的服务器。",
      items: [
        {
          id: "web",
          title: "网页上传工具，所有计划可用",
          body: "它可以同时发送大量照片，远多于 FileZilla（默认同时 2 张，最多不超过 10 张）这样的 FTP 软件，并会持续调整以充分利用您的带宽；连接中断后还会自动续传。所有计划都可以先上传轻量预览图，让您的画廊几分钟内上线。",
        },
        {
          id: "ondemand",
          title: "如果一定要用 FTP：所有计划可用",
          body: "现已推出，所有计划可用：在网页上传工具中启动您自己的按需 FTP 服务器，使用永不改变的个人 Photohawk 地址。无活动 1 小时后自动关闭。",
        },
        {
          id: "studio",
          title: "Photohawk Studio，所有计划可用",
          body: "为您的工作区打造的 Photohawk 上传应用，支持桌面端和 iOS。",
        },
      ],
    },
    privacyLink: "生物特征数据保留政策",
    included: {
      heading: "各计划包含的内容",
      everyPlan:
        "所有计划：自拍搜索、号码布和时间搜索、预览保护、支持预览图优先的网页上传工具，以及您自己的按需 FTP 服务器。",
      upgrades: "Pro 和 Max 增加访客分析和自定义域名。Max 还增加视频上传。",
    },
    compare: {
      heading: "现在与 11月1日起对比",
      subtitle: "逐个计划，以美元计价。",
      rows: {
        fee: "月费",
        commission: "销售佣金",
        storage: "包含的存储空间",
        downloads: "免费下载照片",
        extraStorage: "额外存储空间",
      },
      downloadsToday: "每张 {price}",
      downloadsNext: "每月含 {count} 张，超出后每 1,000 张 $8",
      extraStorageToday: "每 GB 每月 $0.023",
      extraStorageNext: "存储包：+100 GB $5，+1 TB $25，+5 TB $100（每月）",
    },
    countries: {
      heading: "在您的销售地区意味着什么",
      subtitle: "以您的货币举例。请选择国家。",
      selectLabel: "国家",
      examplesBadge: "示例",
      orderHeading: "一笔典型订单：{amount}",
      orderSubtitle: "{inCountry}用银行卡支付的平均订单。银行卡手续费 {fee}，{kind}。",
      kind: {
        stripe: "由 Stripe 向您的账户收取",
        managed: "在 Photohawk 代处理付款时扣除",
      },
      colPlan: "您的计划",
      colCommission: "Photohawk 佣金",
      colKeep: "您拿到",
      perOrderMore: "每笔订单 +{amount}",
      monthlyHeading: "一个月销售的总成本",
      monthlySubtitle:
        "计划费、佣金和银行卡手续费合计占销售额的比例，在每个销售水平下按最省钱的计划计算。",
      level: "每月销售额 {amount}",
      onPlan: "{plan}",
      keepMore: "每月约多拿到 {amount}",
      disclaimer:
        "仅为示例，基于平均订单金额并按固定汇率（2026年10月）换算。实际数字取决于您的订单、银行卡和计划。不包含免费下载和存储费用。",
    },
    faq: {
      heading: "常见问题",
      items: [
        {
          q: "我需要做什么吗？",
          a: "不需要。2026年11月1日，您的工作区会自动转到新的计划。您的画廊、价格和提现照常进行。",
        },
        {
          q: "我在使用 Lite，会怎样？",
          a: "Lite 保留，并且现在免费：自 11月1日起不再收取每月 $16.99，佣金从 18.5% 降至 16%，存储空间也更多：从 100 GB 增至 150 GB。如果存储更多，额外存储每 100 GB 每月 $5 起，绑定银行卡后会自动添加（未绑定时，新上传会暂停，直到您添加银行卡；不会删除任何内容）。您的画廊会保留您的标志和品牌色，并显示一个小小的“Powered by Photohawk”徽章。",
        },
        {
          q: "如果存储量超出计划所含空间怎么办？",
          a: "如果已绑定银行卡，系统会自动添加存储包，每 100 GB 每月 $5 起，按覆盖您存储量的最便宜组合计费。没有绑定银行卡时，新上传会暂停，直到您添加一张：不会删除任何内容，您的画廊也会继续销售。",
        },
        {
          q: "我在使用 Enterprise，会怎样？",
          a: "Enterprise 更名为 Max：同一个计划，新名字。自 11月1日起，您的佣金从 13.5% 降至 12%，存储空间从 1 TB 增至 3 TB。新价格为每月 $49（原为 $44.99），从 11月1日当天或之后的第一次续订起生效。",
        },
        {
          q: "我的工作区可以有多少人？",
          a: "Lite 最多 3 人，Pro 最多 10 人，Max 不限人数。工作区里已经超过这个人数？他们会保留访问权限：人数限制只在您邀请新成员时适用。",
        },
        {
          id: "ftp",
          q: "FTP 会有什么变化？",
          a: "一直在线的共享 FTP 服务器将于 2026年11月1日关闭，网页上传工具现在是所有计划的标准上传方式。如果一定要用 FTP，所有计划的工作区成员现在就可以在网页上传工具中启动自己的按需 FTP 服务器，使用永不改变的个人 Photohawk 地址；无活动 1 小时后自动关闭。",
        },
        {
          q: "我的定制协议会变吗？",
          a: "不会。现有的定制协议在到期前继续有效。",
        },
        {
          q: "如果我更换计划，新佣金何时生效？",
          a: "计划的较低佣金从您使用该计划的第一个完整月开始生效。降级在月底生效。",
        },
        {
          q: "银行卡手续费会变吗？",
          a: "不会。银行卡和支付处理手续费与现在完全相同，与我们的佣金分开计算。",
        },
        {
          q: "Photohawk Friends & Family 属于某个计划吗？",
          a: "不属于。Friends & Family 是我们为您家人的照片打造的应用，是一款即将推出的独立应用，不属于任何 Studio 计划。要管理画廊和上传？请使用 Photohawk Studio：现在所有计划都可以在网页浏览器中使用，适用于 Mac、iPhone 和 iPad 的 Studio 应用将于 2026年11月推出。",
        },
      ],
    },
    cta: {
      heading: "查看完整的新价格",
      body: "计划、存储空间、免费下载和各国银行卡手续费，尽在一页。",
      button: "查看新价格",
    },
  },

  app: {
    badge: "即将推出",
    platform: "首先支持 iPhone",
    heading: "Photohawk Friends & Family，为您家人的照片而生",
    intro: "Friends & Family 是我们为您家人所有照片打造的应用：您拍的照片和您分享的照片。它是一款即将推出的独立应用，不属于 Studio 计划。",
    points: [
      { id: "private", title: "私密地查找家人的照片", body: "直接在手机上整理和查找您所爱之人的照片。" },
      { id: "albums", title: "每个人只看到自己照片的相册", body: "分享一个相册，每个人只会看到通过自拍匹配到的、有自己的照片：与 Photohawk 画廊相同的技术。" },
      { id: "event", title: "汇集大家在活动中拍的照片", body: "把亲友拍的照片汇集到一个地方。" },
      { id: "prints", title: "冲印和相册书", body: "把最好的照片做成冲印和相册书。" },
    ],
    cta: "通知我",
    ctaNote: "候补名单即将开放。",
    phoneAlbum: "只有我",
    phoneAlbumCount: "12 张照片",
  },
};

export default zh;
